import { useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { getCSRF } from '~/services/csrf/csrf-resolver';
import { useCheckoutState } from '~/features/checkout/hooks/useCheckoutState';
import useBasket from '~/features/commerce-api/hooks/useBasket';
import { BetterStackLogLevel, clientLog } from '~/lib/better-stack';
import { ClientSideSingleton as AltaPaySession } from '~/services/altapay/session';
import { parseError } from '~/shared/utils/parse-error';

interface GenerateOrderNumberResponse {
    action: string;
    queryString: string;
    locale: string;
    orderNumber: string;
}

export const useGetOrderNumber = () => {
    const { orderNumber, setOrderNumber } = useCheckoutState();
    const { basketId } = useBasket();
    const hasStarted = useRef(false);

    const generateOrderNumber = async () => {
        const tokenResponse = await getCSRF();

        if (tokenResponse.error)
            throw new Error(`SFCC (generateOrderNumber): CSRF token cannot be generated.`);

        const response = await fetch(
            `/api/salesforce/ocapi/on/demandware.store/Sites-imerco-Site/da_DK/APIService-GenerateOrderNumber?csrf_token=${tokenResponse.csrf.token}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );

        if (!response.ok)
            throw new Error(
                `SFCC (generateOrderNumber): ${response.status} - ${response.statusText}`,
            );

        const gen = (await response.json()) as unknown as GenerateOrderNumberResponse;

        return gen.orderNumber;
    };

    const mutation = useMutation<string>({
        onError: (error) => {
            clientLog({
                event: 'Salesforce:Checkout:OrderNumberGenerationError',
                level: BetterStackLogLevel.WARNING,
                message: `Order number generation failed for basket ${basketId}.`,
                data: {
                    error: parseError(error),
                    sessionId: AltaPaySession.getSessionId(),
                    basketId,
                },
            });
        },
        mutationFn: async () => {
            /**
             * If we have an existing order number saved, let's use that to not need to
             * generate order number every time.
             */
            if (orderNumber) return orderNumber;

            // Else, we'll just generate a new one and save it to storage.
            const generatedOrderNumber = await generateOrderNumber();
            setOrderNumber(generatedOrderNumber);

            return generatedOrderNumber;
        },
    });

    const prepareOrderNumber = useCallback(() => {
        // Strict mode runs effects twice. Two calls would generate two order numbers.
        if (hasStarted.current) return;

        hasStarted.current = true;

        /**
         * Mutations inside useEffect in strict mode get stuck in "pending" state and does not complete.
         * Putting it inside a setTimeout resolves this issue.
         * Ref: https://github.com/TanStack/query/issues/5341#issuecomment-2025571128
         */
        setTimeout(() => mutation.mutate(), 0);
    }, [mutation.mutate]);

    // Reset mutation on hook unmount.
    useEffect(() => {
        return () => mutation.reset();
    }, []);

    return {
        prepareOrderNumber,
        error: mutation.error,
    };
};
